/**
 * EN-1883: every WebApi request by a deactivated or expired user answers
 * 403 { code: 'account_expired', message }. The web treats it like
 * must_change_password: sign out and show the message on Login.
 *
 * Each service makes its own axios instance with axios.create() at module
 * load, and instances do not inherit axios' global interceptors. So this
 * module, imported FIRST in main.ts (before any service), wraps
 * axios.create so every instance gets the response interceptor, and adds it
 * to the default axios instance too. It must not import the store or the
 * router (they import the services, which would then create their
 * instances before the wrapper is installed); main.ts registers the
 * handler with setAccountExpiredHandler instead.
 *
 * The decision logic is pure; e2e/account-expired.unit.spec.ts covers it.
 */
import axios, { AxiosInstance, AxiosStatic } from 'axios';
import { withJsonErrorBody } from './ChatImageError';

export const ACCOUNT_EXPIRED_CODE = 'account_expired';
export const ACCOUNT_EXPIRED_FALLBACK_MESSAGE =
  'Your ProPipe account is no longer active. Contact your company administrator to restore access.';

const INSTALLED = Symbol.for('propipe.accountExpired.installed');
const ATTACHED = Symbol.for('propipe.accountExpired.attached');

let accountExpiredHandler: AccountExpiredHandler | null = null;

/** The message to show for a 403 account_expired, or null for any other error. */
export function accountExpiredMessage(error: unknown): string | null {
  const response = (error as any)?.response;
  if (!response || response.status !== 403) return null;
  const data = response.data;
  if (!data || typeof data !== 'object' || data.code !== ACCOUNT_EXPIRED_CODE)
    return null;
  const message = typeof data.message === 'string' ? data.message.trim() : '';
  return message || ACCOUNT_EXPIRED_FALLBACK_MESSAGE;
}

export interface AccountExpiredState {
  /** A user is signed in (store.state.user). */
  signedIn: boolean;
  /** The current route is login / loginRedirect. */
  onLoginRoute: boolean;
}

export interface AccountExpiredPlan {
  logout: boolean;
  redirect: boolean;
  message: string;
}

/**
 * What to do for an error: null when it is not account_expired, or when the
 * user is already signed out (a late reply of a request sent before the
 * logout), so there is one logout, one message and no redirect loop.
 */
export function accountExpiredPlan(
  error: unknown,
  state: AccountExpiredState
): AccountExpiredPlan | null {
  const message = accountExpiredMessage(error);
  if (message === null || !state.signedIn) return null;
  return { logout: true, redirect: !state.onLoginRoute, message };
}

/**
 * The error to hand to the account_expired handler, or null when it is not
 * account_expired. With responseType 'blob' (chat images, moderation
 * attachments) axios leaves the JSON body as a Blob; then this decodes a
 * copy, so the handler can read the code while the caller's error and its
 * Blob stay exactly as they were.
 */
export async function accountExpiredError(
  error: unknown
): Promise<unknown | null> {
  if (accountExpiredMessage(error) !== null) return error;
  const response = (error as any)?.response;
  if (!response || response.status !== 403 || !isBlobLike(response.data))
    return null;
  // withJsonErrorBody replaces response.data in place, so give it a copy;
  // Blob.text() does not consume the Blob, the caller can still read it.
  const copy = { response: { ...response } };
  const decoded = await withJsonErrorBody(copy);
  return accountExpiredMessage(decoded) !== null ? decoded : null;
}

function isBlobLike(data: unknown): boolean {
  return (
    typeof (data as { text?: unknown } | null | undefined)?.text === 'function'
  );
}

export type AccountExpiredHandler = (error: unknown) => void;

/** Called for every account_expired reply (main.ts sets it). */
export function setAccountExpiredHandler(
  handler: AccountExpiredHandler | null
) {
  accountExpiredHandler = handler;
}

function runHandler(error: unknown) {
  if (!accountExpiredHandler) return;
  try {
    accountExpiredHandler(error);
  } catch (handlerError) {
    console.error('account_expired handler failed', handlerError);
  }
}

function onResponseError(error: unknown) {
  if (accountExpiredMessage(error) !== null) {
    // the usual JSON body: handled synchronously, before the caller's catch
    runHandler(error);
  } else if (isBlobLike((error as any)?.response?.data)) {
    // EN-1883 review S1: a responseType 'blob' body has to be read first
    return accountExpiredError(error).then((decoded) => {
      if (decoded !== null) runHandler(decoded);
      return Promise.reject(error);
    });
  }
  // the caller still sees the error (its own catch / messages run as before)
  return Promise.reject(error);
}

/** Adds the response interceptor to one instance (once). */
export function attachAccountExpiredInterceptor<T extends AxiosInstance>(
  instance: T
): T {
  const target = instance as any;
  if (target && !target[ATTACHED] && target.interceptors?.response) {
    target.interceptors.response.use(undefined, onResponseError);
    target[ATTACHED] = true;
  }
  return instance;
}

/**
 * Wraps axiosStatic.create so every instance created afterwards gets the
 * interceptor, and attaches it to the default instance. Idempotent.
 */
export function installAccountExpiredInterceptor(axiosStatic: AxiosStatic) {
  const target = axiosStatic as any;
  if (!target || target[INSTALLED]) return;
  target[INSTALLED] = true;
  attachAccountExpiredInterceptor(axiosStatic);
  const create = axiosStatic.create;
  target.create = function createWithAccountExpired(
    this: unknown,
    ...args: Parameters<AxiosStatic['create']>
  ) {
    return attachAccountExpiredInterceptor(create.apply(this, args));
  };
}

installAccountExpiredInterceptor(axios);
